import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Calendar,
  MapPin,
  Tag,
  Globe,
  CheckCircle,
  Save,
} from "lucide-react";
import { adminAPI } from "../../services/api";
import "./Admin.css";

const AdminCreateEvent = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    start_date: "",
    end_date: "",
    location: "",
    primary_color: "#003366",
    accent_color: "#E85D04",
  });

  const isEditMode = !!eventId;

  useEffect(() => {
    if (isEditMode) {
      fetchEventData();
    }
  }, [eventId]);

  const fetchEventData = async () => {
    setFetching(true);
    try {
      const res = await adminAPI.getEvent(eventId);
      const data = res.data;
      
      // Format dates for datetime-local input (YYYY-MM-DDTHH:mm) in local time
      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        // Offset in minutes, convert to ms
        const offset = date.getTimezoneOffset() * 60000;
        // Shift global time to local time representation for ISO string
        return new Date(date.getTime() - offset).toISOString().slice(0, 16);
      };

      setFormData({
        title: data.title || "",
        slug: data.slug || "",
        start_date: formatDate(data.start_date),
        end_date: formatDate(data.end_date),
        location: data.location || "",
        primary_color: data.primary_color || "#003366",
        accent_color: data.accent_color || "#E85D04",
      });
    } catch (err) {
      console.error("Failed to fetch event data", err);
      alert("Failed to load event data. Please try again.");
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create a copy of formData and convert dates to UTC ISO strings
      const submissionData = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : undefined,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : undefined,
      };

      if (isEditMode) {
        await adminAPI.updateEvent(eventId, submissionData);
        alert("Event updated successfully!");
      } else {
        await adminAPI.createEvent(submissionData);
        alert("Event created successfully!");
      }
      navigate("/"); // Go back to selector
    } catch (err) {
      console.error("Failed to save event", err);
      alert(isEditMode ? "Failed to update event." : "Failed to create event. Please check if the slug is unique.");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="admin-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="loader-spinner"></div>
      </div>
    );
  }

  return (
    <div
      className="admin-page"
      style={{ background: "#f8fafc", minHeight: "100vh", padding: "0" }}
    >
      {/* Premium Header */}
      <div
        style={{
          background: "white",
          padding: "20px 32px",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: "20px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate("/")}
          style={{
            background: "#f1f5f9",
            border: "none",
            width: "40px",
            height: "40px",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#64748b",
          }}
        >
          <ChevronLeft size={24} />
        </button>
        <h1
          style={{
            fontSize: "1.25rem",
            fontWeight: "800",
            margin: 0,
            color: "#0f172a",
            fontFamily: "Outfit, sans-serif",
          }}
        >
          {isEditMode ? "Update Event" : "Create New Event"}
        </h1>
      </div>

      <div
        style={{ maxWidth: "800px", margin: "40px auto", padding: "0 24px" }}
      >
        <div
          className="admin-card"
          style={{
            padding: "32px",
            borderRadius: "24px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
              }}
            >
              {/* Event Title */}
              <div style={{ gridColumn: "span 2" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  <Globe size={14} style={{ marginRight: "6px" }} /> Event Title
                </label>
                <input
                  required
                  value={formData.title}
                  placeholder="e.g. Reliance Foundation AGM 2026"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    outline: "none",
                    fontSize: "1rem",
                  }}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                />
              </div>

              {/* Slug */}
              <div style={{ gridColumn: "span 2" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  <Tag size={14} style={{ marginRight: "6px" }} /> Event Slug
                  (Unique identifier)
                </label>
                <input
                  required
                  value={formData.slug}
                  placeholder="e.g. rf-agm-2026"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    outline: "none",
                    fontSize: "1rem",
                    background: isEditMode ? "#f1f5f9" : "#f8fafc",
                    cursor: isEditMode ? "not-allowed" : "text",
                  }}
                  disabled={isEditMode}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                />
                {isEditMode && (
                  <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                    The unique identifier cannot be changed after creation to prevent broken links.
                  </p>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  <Calendar size={14} style={{ marginRight: "6px" }} /> Start
                  Date & Time
                </label>
                <input
                  required
                  type="datetime-local"
                  value={formData.start_date}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    outline: "none",
                    fontSize: "1rem",
                  }}
                  onChange={(e) =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                />
              </div>

              {/* End Date */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  <Calendar size={14} style={{ marginRight: "6px" }} /> End Date
                  & Time
                </label>
                <input
                  required
                  type="datetime-local"
                  value={formData.end_date}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    outline: "none",
                    fontSize: "1rem",
                  }}
                  onChange={(e) =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                />
              </div>

              {/* Location */}
              <div style={{ gridColumn: "span 2" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  <MapPin size={14} style={{ marginRight: "6px" }} /> Location
                </label>
                <input
                  required
                  value={formData.location}
                  placeholder="e.g. Jio World Centre, Mumbai"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    border: "1.5px solid #e2e8f0",
                    outline: "none",
                    fontSize: "1rem",
                  }}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                />
              </div>

              {/* Branding Colors */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  Primary Color
                </label>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <input
                    type="color"
                    value={formData.primary_color}
                    style={{
                      width: "44px",
                      height: "44px",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0",
                    }}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        primary_color: e.target.value,
                      })
                    }
                  />
                  <span style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                    {formData.primary_color}
                  </span>
                </div>
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  Accent Color
                </label>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <input
                    type="color"
                    value={formData.accent_color}
                    style={{
                      width: "44px",
                      height: "44px",
                      border: "none",
                      borderRadius: "8px",
                      padding: "0",
                    }}
                    onChange={(e) =>
                      setFormData({ ...formData, accent_color: e.target.value })
                    }
                  />
                  <span style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                    {formData.accent_color}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "40px" }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "16px",
                  background:
                    "linear-gradient(135deg, #003366 0%, #0056b3 100%)",
                  color: "white",
                  border: "none",
                  fontSize: "1rem",
                  fontWeight: "700",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  boxShadow: "0 8px 20px rgba(0, 51, 102, 0.2)",
                  transition: "transform 0.2s",
                }}
                onMouseDown={(e) =>
                  (e.currentTarget.style.transform = "scale(0.98)")
                }
                onMouseUp={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                {loading ? (
                  isEditMode ? "Updating..." : "Creating..."
                ) : (
                  <>
                    {isEditMode ? <Save size={20} /> : <CheckCircle size={20} />}
                    {isEditMode ? "Update Event" : "Create Global Event"}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateEvent;
